import { Blueprint } from '@rta/rta-blueprint-library';

import { clearHighlights } from './ai-tools/clear-highlights.tool';
import { clearSelections } from './ai-tools/clear-selections.tool';
import { getApplianceSelection } from './ai-tools/get-appliance-selection.tool';
import { getFloorOptions } from './ai-tools/get-floor-options.tool';
import { getIslandSelection } from './ai-tools/get-island-selection.tool';
import { getSkyOptions } from './ai-tools/get-sky-options.tool';
import { highlightObject } from './ai-tools/highlight-object.tool';
import { lookAtObject } from './ai-tools/look-at-object.tool';
import { resetView } from './ai-tools/reset-view.tool';
import { rotateCamera } from './ai-tools/rotate-camera.tool';
import { setDebugMode } from './ai-tools/set-debug-mode.tool';
import { setFloorTexture } from './ai-tools/set-floor-texture.tool';
import { setGeoLocation } from './ai-tools/set-geo-location.tool';
import { setRenderQuality } from './ai-tools/set-render-quality.tool';
import { setSkyTexture } from './ai-tools/set-sky-texture.tool';
import { setTextureQuality } from './ai-tools/set-texture-quality.tool';
import { setTimeOfDay } from './ai-tools/set-time-of-day.tool';
import { setWeather } from './ai-tools/set-weather.tool';
import { takeScreenshot } from './ai-tools/take-screenshot.tool';
import { ToolContext } from './tool-context.type';
import { ToolResult } from '../types/tool.types';
import { zoomTo } from './ai-tools/zoom-to.tool';

// Tool names handled by the 3D controller
const CONTROLLER_TOOL_NAMES = [
	'rotate_camera',
	'look_at_object',
	'reset_view',
	'zoom_to',
	'set_render_quality',
	'set_texture_quality',
	'set_debug_mode',
	'highlight_object',
	'clear_highlights',
	'take_screenshot',
	'get_floor_options',
	'get_sky_options',
	'set_floor_texture',
	'set_sky_texture',
	'set_geo_location',
	'set_time_of_day',
	'set_weather',
	'get_island_selection',
	'get_appliance_selection',
	'clear_selections',
	'rotate_appliance'
] as const;

export const executeTool = async (
	name: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
	args: any,
	context: ToolContext,
	blueprint?: Blueprint
// eslint-disable-next-line max-params
): Promise<ToolResult> => {
	const isControllerTool = (CONTROLLER_TOOL_NAMES as readonly string[]).includes(name);

	// Controller tools require Three.js instance (except info/query tools)
	const noThreeRequired = ['set_render_quality', 'set_texture_quality', 'get_floor_options', 'get_sky_options', 'reset_view'];
	if (isControllerTool && !context.threeInstance && !noThreeRequired.includes(name)) {
		return {
			success: false,
			error: 'Three.js instance not initialized. Please wait for the 3D scene to load.'
		};
	}

	try {
		switch (name) {
			case 'rotate_camera':
				return await rotateCamera(args, context);
			case 'look_at_object':
				return await lookAtObject(args, context);
			case 'reset_view':
				return await resetView(args, context);
			case 'zoom_to':
				return await zoomTo(args, context);
			case 'set_render_quality':
				return await setRenderQuality(args, context);
			case 'set_texture_quality':
				return await setTextureQuality(args, context);
			case 'set_debug_mode':
				return await setDebugMode(args, context);
			case 'highlight_object':
				return await highlightObject(args, context);
			case 'clear_highlights':
				return await clearHighlights(args, context);
			case 'take_screenshot':
				return await takeScreenshot(args, context);
			case 'get_floor_options':
				return await getFloorOptions();
			case 'get_sky_options':
				return await getSkyOptions();
			case 'set_floor_texture':
				return await setFloorTexture(args, context);
			case 'set_sky_texture':
				return await setSkyTexture(args, context);
			case 'set_geo_location':
				return await setGeoLocation(args, context);
			case 'set_time_of_day':
				return await setTimeOfDay(args, context);
			case 'set_weather':
				return await setWeather(args, context);
			case 'get_island_selection':
				return await getIslandSelection(args, context);
			case 'get_appliance_selection':
				return await getApplianceSelection(args, context);
			case 'clear_selections':
				return await clearSelections(args, context);
			case 'rotate_appliance': {
				const { objectId, axis, angle } = args;
				if (context.controller?.rotateAppliance) {
					context.controller.rotateAppliance({ objectId, axis, angle });
					return { success: true, message: `Rotated ${objectId} on ${axis} by ${angle} rad` };
				}
				return { success: false, error: 'Controller does not support rotateAppliance' };
			}
			default:
				// Not a controller tool - proxy to Blueprint if available
				if (blueprint) {
					return await blueprint.executeTool(name, args);
				}
				// No Blueprint - noop (silent success)
				return { success: true };
		}
	} catch (error: any) {
		return {
			success: false,
			errorCode: 'TOOL_EXECUTION_ERROR',
			error: error?.message || 'An unexpected error occurred.'
		};
	}
};
